import React, { useRef, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Box, useAppTheme } from '@workwhile/ui'
import { isSameYear } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone } from 'lib/time'
import {
  TimeConversionContent,
  TimeConversionZone,
} from './TimeConversionContent'

const HOVER_CLOSE_DELAY_MS = 120

export type TimeConversionPopoverProps = {
  value: string | Date
  zones: TimeConversionZone[]
  /** Timezone used for the trigger label when children are not provided. */
  primaryTimeZone?: string
  children?: React.ReactNode
  side?: React.ComponentProps<typeof Popover.Content>['side']
  align?: React.ComponentProps<typeof Popover.Content>['align']
}

const formatPrimaryTimestamp = (
  value: string | Date,
  timeZone: string
): string => {
  const isCurrentYear = isSameYear(value, new Date())
  return formatInTimeZone(
    value,
    timeZone,
    isCurrentYear ? 'EEE MMM, dd, hh:mm a zz' : 'EEE MMM, dd, yyyy, hh:mm a zz'
  )
}

export const TimeConversionPopover = ({
  value,
  zones,
  primaryTimeZone = getLocalTimezone(),
  children,
  side = 'bottom',
  align = 'start',
}: TimeConversionPopoverProps) => {
  const { colors, radii } = useAppTheme()
  const [open, setOpen] = useState(false)
  const closeTimeoutRef = useRef<number | null>(null)
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false)
    }, HOVER_CLOSE_DELAY_MS)
  }

  const handleOpen = () => {
    clearCloseTimeout()
    setOpen(true)
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Box
          as="span"
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
          style={{
            color: 'inherit',
            cursor: 'help',
            display: 'inline',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textDecorationColor: colors.neutrals[300],
            textUnderlineOffset: '3px',
          }}
        >
          {children ?? formatPrimaryTimestamp(value, primaryTimeZone)}
        </Box>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={8}
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
          style={{ zIndex: 10005 }}
        >
          <Box
            backgroundColor={colors.white}
            border="1px solid rgba(0, 0, 0, 0.08)"
            borderRadius={radii.standard}
            px={3}
            py={2}
            style={{
              boxShadow:
                '0 0 0 1px rgba(0, 0, 0, 0.04), 0 7px 14px rgba(60, 66, 87, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04)',
            }}
          >
            <TimeConversionContent value={value} zones={zones} />
          </Box>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
